/**
 * File: streamingChatService.ts
 * Description: Streaming chat service for assistant-ui integration
 * Author: System
 * Created: 2025-09-03
 * Last Modified: 2025-09-03
 */

import { API_CONFIG } from '../config/api';
import { ChatAction, ChatRequest } from '../types/chat';
import { KeyValue } from '../types/context';
import { logger } from '../utils/logger';

import { getAccessToken } from './accessTokenManager';

interface ToolCall {
  tool_name: string;
  tool_input: string;
  tool_output: string;
}

interface AgentExecutionStep {
  agent_name: string;
  reasoning: string;
  tool_calls: ToolCall[];
}

interface StreamEvent {
  type: 'text-delta' | 'finish' | 'error' | 'tool_call' | 'session' | 'actions' | 'action';
  textDelta?: string;
  finishReason?: string;
  error?: string;
  data?: AgentExecutionStep;
  session_id?: string;
  actions?: ChatAction[];
  action?: ChatAction;
}

/**
 * Streaming chat service for real-time assistant-ui integration
 */
export class StreamingChatService {
  private baseUrl: string;
  private currentSessionId: string | null = null;

  constructor() {
    // Use same base URL as your existing API client
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  /**
   * Get the current session ID
   */
  public getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Set the current session ID
   */
  public setCurrentSessionId(sessionId: string | null): void {
    this.currentSessionId = sessionId;
  }

  /**
   * Create SSE stream for chat messages with tool call support
   */
  public async *streamMessage(
    projectId: number,
    userInput: string,
    context: KeyValue[],
    sessionId?: string,
  ): AsyncGenerator<
    { type: 'text' | 'tool-call' | 'session' | 'actions'; content: string | AgentExecutionStep | ChatAction[] | { session_id: string } }, 
    void, 
    unknown
  > {
    // For testing: use a default project ID if none provided
    const actualProjectId = projectId || 1;
    const url = `${this.baseUrl}/api/project/${actualProjectId}/chat/stream`;
    
    const requestPayload: ChatRequest = {
      user_input: userInput.trim(),
      context: this.sanitizeContext(context),
      session_id: sessionId || this.currentSessionId || undefined,
    };

    // Get auth token same way as ApiClient
    const token = getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Failed to get response reader');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          
          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6); // Remove 'data: ' prefix
              
              if (data.trim() === '') {
                continue; // Skip empty data lines
              }

              try {
                const event: StreamEvent = JSON.parse(data);
                
                
                if (event.type === 'text-delta' && event.textDelta) {
                  yield { type: 'text', content: event.textDelta };
                } else if (event.type === 'tool_call' && event.data) {
                  yield { type: 'tool-call', content: event.data };
                } else if (event.type === 'actions' && event.actions) {
                  yield { type: 'actions', content: event.actions };
                } else if (event.type === 'action' && event.action) {
                  // Handle single action from tool output
                  yield { type: 'actions', content: [event.action] };
                } else if (event.type === 'session' && event.session_id) {
                  this.setCurrentSessionId(event.session_id);
                  yield { type: 'session', content: { session_id: event.session_id } };
                } else if (event.type === 'error') {
                  throw new Error(event.error || 'Unknown streaming error');
                } else if (event.type === 'finish') {
                  // Stream complete
                  return;
                }
              } catch (parseError) {
                logger.warn('Failed to parse SSE data:', data, parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      logger.error('Streaming chat error:', error);
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'An error occurred while streaming the response.';
      
      yield { type: 'text', content: `**Error:** ${errorMessage}` };
    }
  }

  /**
   * Sanitize context data before sending to backend
   */
  private sanitizeContext(context: KeyValue[]): KeyValue[] {
    return context
      .filter(item => 
        item.key && 
        item.value && 
        typeof item.key === 'string' && 
        typeof item.value === 'string',
      )
      .map(item => ({
        key: item.key.trim(),
        value: this.sanitizeValue(item.value),
      }))
      .filter(item => item.key.length > 0 && item.value.length > 0)
      .slice(0, 50); // Limit context size
  }

  /**
   * Sanitize individual context values
   */
  private sanitizeValue(value: string): string {
    return value
      .trim()
      .substring(0, 1000) // Limit individual value size
      .replace(/[\r\n\t]+/g, ' ') // Replace newlines/tabs with spaces
      .replace(/\s+/g, ' '); // Replace multiple spaces with single space
  }
}

// Export singleton instance
export const streamingChatService = new StreamingChatService();
export default streamingChatService;
